import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import SettingsCard from '../../components/SettingsCard';
import SearchField from '../../components/SearchField';
import { useDialogRef, dialogPanelProps } from '../../components/a11y';
import useAccounts from '@/src/Accounts/components/hooks/useAccounts';
import useCreateAccount from '@/src/Accounts/components/hooks/useCreateAccount';
import useEditAccount from '@/src/Accounts/components/hooks/useEditAccount';
import useAuth from '@/src/Authentication/components/hooks/useAuth';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';

/*
 * Org accounts — who can sign in to this organization. Classic's
 * /accounts CRUD in the SettingsCard grammar: search, role chips,
 * hover actions, create/edit/password dialogs. Roles are enforced
 * server-side (ensureAdmin on every accounts route).
 */

interface AccountRow {
    id: number;
    username: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    accountRoleId?: number;
}

const ROLE_LABELS: Record<number, string> = {
    0: 'ADMIN',
    1: 'USER',
    2: 'CLIENT',
    69: 'GLOBAL',
};

const displayName = (a: AccountRow) =>
    [a.firstName, a.lastName].filter(Boolean).join(' ') || a.username;

function Dialog({
    label,
    onClose,
    children,
}: {
    label: string;
    onClose: () => void;
    children: React.ReactNode;
}) {
    const ref = useDialogRef(true, onClose);
    return (
        <div
            className="psv2 fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
            onClick={onClose}
        >
            <div
                ref={ref}
                {...dialogPanelProps(label)}
                className="w-full max-w-[400px] rounded-[15px] border border-border bg-background p-5 shadow-xl outline-none"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="ps-label mb-3.5">{label}</div>
                {children}
            </div>
        </div>
    );
}

const FIELD_LABEL =
    'mb-1.5 block text-[12px] font-semibold text-muted-foreground';

export default function OrgAccountsV2() {
    const { getAccounts } = useAccounts();
    const { createAccount } = useCreateAccount();
    const { editAccount, setPassword } = useEditAccount();
    const { auth } = useAuth() as any;
    const { orgId } = useUserOrg();
    const authenticatedApi = useAuthenticatedApi();

    const [accounts, setAccounts] = useState<AccountRow[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [dialog, setDialog] = useState<
        | { kind: 'create' }
        | { kind: 'edit'; account: AccountRow }
        | { kind: 'password'; account: AccountRow }
        | null
    >(null);
    const [draft, setDraft] = useState({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        confirm: '',
        role: 1,
    });
    const [busy, setBusy] = useState(false);
    const [dialogError, setDialogError] = useState<string | null>(null);

    const load = async () => {
        const rows = (await getAccounts().catch(() => [])) ?? [];
        setAccounts(rows);
        setIsLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return accounts;
        return accounts.filter((a) =>
            `${a.username} ${a.email ?? ''} ${displayName(a)}`
                .toLowerCase()
                .includes(q),
        );
    }, [accounts, search]);

    const openCreate = () => {
        setDraft({
            username: '',
            email: '',
            firstName: '',
            lastName: '',
            password: '',
            confirm: '',
            role: 1,
        });
        setDialogError(null);
        setDialog({ kind: 'create' });
    };

    const openEdit = (account: AccountRow) => {
        setDraft({
            username: account.username ?? '',
            email: account.email ?? '',
            firstName: account.firstName ?? '',
            lastName: account.lastName ?? '',
            password: '',
            confirm: '',
            role: account.accountRoleId ?? 1,
        });
        setDialogError(null);
        setDialog({ kind: 'edit', account });
    };

    const openPassword = (account: AccountRow) => {
        setDraft((d) => ({ ...d, password: '', confirm: '' }));
        setDialogError(null);
        setDialog({ kind: 'password', account });
    };

    const submitCreate = async () => {
        if (busy) return;
        setBusy(true);
        setDialogError(null);
        const response = await createAccount({
            username: draft.username.trim(),
            email: draft.email.trim(),
            firstName: draft.firstName.trim(),
            lastName: draft.lastName.trim(),
            password: draft.password,
            role: draft.role,
            orgId,
        });
        setBusy(false);
        if (response?.status === 200 || response?.data?.id) {
            setDialog(null);
            toast.success('Account created');
            load();
        } else {
            setDialogError(
                response?.message ?? "Couldn't create the account.",
            );
        }
    };

    const submitEdit = async () => {
        if (dialog?.kind !== 'edit' || busy) return;
        setBusy(true);
        setDialogError(null);
        const ok = await editAccount({
            id: dialog.account.id,
            username: draft.username.trim(),
            email: draft.email.trim(),
            firstName: draft.firstName.trim(),
            lastName: draft.lastName.trim(),
            role: draft.role,
        }).catch(() => false);
        setBusy(false);
        if (ok) {
            setDialog(null);
            toast.success('Saved');
            load();
        } else {
            setDialogError("Couldn't save — try again.");
        }
    };

    const submitPassword = async () => {
        if (dialog?.kind !== 'password' || busy) return;
        if (draft.password.length === 0 || draft.password !== draft.confirm) {
            setDialogError('Passwords must match.');
            return;
        }
        setBusy(true);
        setDialogError(null);
        const ok = await setPassword(dialog.account.id, draft.password).catch(
            () => false,
        );
        setBusy(false);
        if (ok) {
            setDialog(null);
            toast.success('Password updated');
        } else {
            setDialogError("Couldn't update the password.");
        }
    };

    const removeAccount = async (account: AccountRow) => {
        if (
            !window.confirm(
                `Delete the account "${account.username}"? They lose dashboard access immediately.`,
            )
        )
            return;
        const prev = accounts;
        setAccounts((rows) => rows.filter((r) => r.id !== account.id));
        const ok = await authenticatedApi
            .delete(`/accounts/${account.id}`)
            .then(() => true)
            .catch(() => false);
        if (!ok) {
            setAccounts(prev);
            toast.error("Couldn't delete the account.");
        }
    };

    const canSubmitCreate =
        draft.username.trim() &&
        draft.email.trim() &&
        draft.firstName.trim() &&
        draft.lastName.trim() &&
        draft.password.length > 0;

    if (isLoading) {
        return (
            <div className="mb-6 h-40 animate-pulse rounded-[16px] bg-secondary" />
        );
    }

    return (
        <>
            <SettingsCard
                title="Accounts"
                desc="Who can sign in to this organization."
                foot={
                    <>
                        <span className="flex-1 text-[11.5px] text-faint">
                            {accounts.length} account
                            {accounts.length === 1 ? '' : 's'}
                        </span>
                        <button
                            type="button"
                            onClick={openCreate}
                            className="cursor-pointer rounded-full border-0 bg-foreground px-[18px] py-2 font-[inherit] text-[12.5px] font-semibold text-background"
                        >
                            Add account
                        </button>
                    </>
                }
            >
                {accounts.length > 5 && (
                    <SearchField
                        className="mb-2 w-[220px]"
                        placeholder="Search accounts…"
                        value={search}
                        onChange={setSearch}
                    />
                )}
                {filtered.length === 0 && (
                    <p className="py-2 text-[13px] text-muted-foreground">
                        {search.trim()
                            ? 'No accounts match.'
                            : 'No accounts yet.'}
                    </p>
                )}
                {filtered.map((account) => {
                    const self = account.username === auth?.username;
                    return (
                        <div
                            key={account.id}
                            className="group flex items-center gap-3 border-b border-line py-[9px] text-[12.5px] last:border-b-0"
                        >
                            <span className="w-[150px] flex-none truncate font-semibold">
                                {displayName(account)}
                            </span>
                            <span className="min-w-0 flex-1 truncate text-muted-foreground">
                                {account.email || account.username}
                            </span>
                            <span
                                className={
                                    'rounded-full border px-2 py-[2px] font-mono text-[9px] font-semibold tracking-[0.1em] ' +
                                    (account.accountRoleId === 0 ||
                                    account.accountRoleId === 69
                                        ? 'border-brand-soft bg-brand-soft text-brand-ink'
                                        : 'border-border text-muted-foreground')
                                }
                            >
                                {ROLE_LABELS[account.accountRoleId ?? -1] ??
                                    '—'}
                            </span>
                            <span className="flex gap-3 text-[12px] font-semibold opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                                <button
                                    type="button"
                                    onClick={() => openEdit(account)}
                                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-muted-foreground hover:text-foreground"
                                >
                                    edit
                                </button>
                                <button
                                    type="button"
                                    onClick={() => openPassword(account)}
                                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-muted-foreground hover:text-foreground"
                                >
                                    password
                                </button>
                                {!self && (
                                    <button
                                        type="button"
                                        title="Delete account"
                                        onClick={() =>
                                            removeAccount(account)
                                        }
                                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-faint hover:text-attention"
                                    >
                                        ×
                                    </button>
                                )}
                            </span>
                        </div>
                    );
                })}
            </SettingsCard>

            {dialog?.kind === 'create' && (
                <Dialog label="Add account" onClose={() => setDialog(null)}>
                    <div className="mb-3 grid grid-cols-2 gap-3">
                        <div>
                            <label className={FIELD_LABEL}>First name</label>
                            <input
                                autoFocus
                                className="ps-input w-full text-[13px]"
                                value={draft.firstName}
                                onChange={(e) =>
                                    setDraft((d) => ({
                                        ...d,
                                        firstName: e.target.value,
                                    }))
                                }
                            />
                        </div>
                        <div>
                            <label className={FIELD_LABEL}>Last name</label>
                            <input
                                className="ps-input w-full text-[13px]"
                                value={draft.lastName}
                                onChange={(e) =>
                                    setDraft((d) => ({
                                        ...d,
                                        lastName: e.target.value,
                                    }))
                                }
                            />
                        </div>
                    </div>
                    <label className={FIELD_LABEL}>Username</label>
                    <input
                        className="ps-input mb-3 w-full text-[13px]"
                        value={draft.username}
                        onChange={(e) =>
                            setDraft((d) => ({
                                ...d,
                                username: e.target.value,
                            }))
                        }
                    />
                    <label className={FIELD_LABEL}>Email</label>
                    <input
                        type="email"
                        className="ps-input mb-3 w-full text-[13px]"
                        value={draft.email}
                        onChange={(e) =>
                            setDraft((d) => ({
                                ...d,
                                email: e.target.value,
                            }))
                        }
                    />
                    <div className="mb-4 grid grid-cols-2 gap-3">
                        <div>
                            <label className={FIELD_LABEL}>Password</label>
                            <input
                                type="password"
                                className="ps-input w-full text-[13px]"
                                value={draft.password}
                                onChange={(e) =>
                                    setDraft((d) => ({
                                        ...d,
                                        password: e.target.value,
                                    }))
                                }
                            />
                        </div>
                        <div>
                            <label className={FIELD_LABEL}>Role</label>
                            <select
                                className="ps-input w-full cursor-pointer appearance-none text-[13px]"
                                value={draft.role}
                                onChange={(e) =>
                                    setDraft((d) => ({
                                        ...d,
                                        role: +e.target.value,
                                    }))
                                }
                            >
                                <option value={0}>Admin</option>
                                <option value={1}>User</option>
                                <option value={2}>Client</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            disabled={!canSubmitCreate || busy}
                            onClick={submitCreate}
                            className="cursor-pointer rounded-full border-0 bg-brand px-5 py-2 font-[inherit] text-[13px] font-semibold text-brand-foreground disabled:opacity-50"
                        >
                            {busy ? 'Creating…' : 'Create account'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setDialog(null)}
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground"
                        >
                            Cancel
                        </button>
                        {dialogError && (
                            <span className="text-[12px] font-semibold text-attention">
                                {dialogError}
                            </span>
                        )}
                    </div>
                </Dialog>
            )}

            {dialog?.kind === 'edit' && (
                <Dialog
                    label={`Edit ${dialog.account.username}`}
                    onClose={() => setDialog(null)}
                >
                    <div className="mb-3 grid grid-cols-2 gap-3">
                        <div>
                            <label className={FIELD_LABEL}>First name</label>
                            <input
                                autoFocus
                                className="ps-input w-full text-[13px]"
                                value={draft.firstName}
                                onChange={(e) =>
                                    setDraft((d) => ({
                                        ...d,
                                        firstName: e.target.value,
                                    }))
                                }
                            />
                        </div>
                        <div>
                            <label className={FIELD_LABEL}>Last name</label>
                            <input
                                className="ps-input w-full text-[13px]"
                                value={draft.lastName}
                                onChange={(e) =>
                                    setDraft((d) => ({
                                        ...d,
                                        lastName: e.target.value,
                                    }))
                                }
                            />
                        </div>
                    </div>
                    <label className={FIELD_LABEL}>Username</label>
                    <input
                        className="ps-input mb-3 w-full text-[13px]"
                        value={draft.username}
                        onChange={(e) =>
                            setDraft((d) => ({
                                ...d,
                                username: e.target.value,
                            }))
                        }
                    />
                    <label className={FIELD_LABEL}>Email</label>
                    <input
                        type="email"
                        className="ps-input mb-3 w-full text-[13px]"
                        value={draft.email}
                        onChange={(e) =>
                            setDraft((d) => ({
                                ...d,
                                email: e.target.value,
                            }))
                        }
                    />
                    <label className={FIELD_LABEL}>Role</label>
                    <select
                        className="ps-input mb-4 w-full cursor-pointer appearance-none text-[13px]"
                        value={draft.role}
                        onChange={(e) =>
                            setDraft((d) => ({
                                ...d,
                                role: +e.target.value,
                            }))
                        }
                    >
                        <option value={0}>Admin</option>
                        <option value={1}>User</option>
                        <option value={2}>Client</option>
                        {draft.role === 69 && (
                            <option value={69}>GlobalAdmin</option>
                        )}
                    </select>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            disabled={busy || !draft.username.trim()}
                            onClick={submitEdit}
                            className="cursor-pointer rounded-full border-0 bg-brand px-5 py-2 font-[inherit] text-[13px] font-semibold text-brand-foreground disabled:opacity-50"
                        >
                            {busy ? 'Saving…' : 'Save'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setDialog(null)}
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground"
                        >
                            Cancel
                        </button>
                        {dialogError && (
                            <span className="text-[12px] font-semibold text-attention">
                                {dialogError}
                            </span>
                        )}
                    </div>
                </Dialog>
            )}

            {dialog?.kind === 'password' && (
                <Dialog
                    label={`Set password — ${dialog.account.username}`}
                    onClose={() => setDialog(null)}
                >
                    <label className={FIELD_LABEL}>New password</label>
                    <input
                        autoFocus
                        type="password"
                        className="ps-input mb-3 w-full text-[13px]"
                        value={draft.password}
                        onChange={(e) =>
                            setDraft((d) => ({
                                ...d,
                                password: e.target.value,
                            }))
                        }
                    />
                    <label className={FIELD_LABEL}>Confirm password</label>
                    <input
                        type="password"
                        className="ps-input mb-4 w-full text-[13px]"
                        value={draft.confirm}
                        onChange={(e) =>
                            setDraft((d) => ({
                                ...d,
                                confirm: e.target.value,
                            }))
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') submitPassword();
                        }}
                    />
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            disabled={busy || draft.password.length === 0}
                            onClick={submitPassword}
                            className="cursor-pointer rounded-full border-0 bg-brand px-5 py-2 font-[inherit] text-[13px] font-semibold text-brand-foreground disabled:opacity-50"
                        >
                            {busy ? 'Saving…' : 'Set password'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setDialog(null)}
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground"
                        >
                            Cancel
                        </button>
                        {dialogError && (
                            <span className="text-[12px] font-semibold text-attention">
                                {dialogError}
                            </span>
                        )}
                    </div>
                </Dialog>
            )}
        </>
    );
}
